<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import { FocusScope } from 'reka-ui'

type WizardOverlayTheme = 'default' | 'green' | 'gray'

const THEME_CLASSES: Record<WizardOverlayTheme, string> = {
  default: 'bg-blue-100',
  green: 'bg-green-100',
  gray: 'bg-gray-200'
}

const props = withDefaults(
  defineProps<{
    activated?: boolean
    theme?: WizardOverlayTheme
    label?: string
  }>(),
  { activated: false, theme: 'default', label: '' }
)

const emit = defineEmits<{ unfocus: [] }>()

const overlay = ref<InstanceType<typeof FocusScope> | null>(null)
const elementFocusedBeforeActivation = ref<HTMLElement | null>(null)

// reka-ui's FocusScope stays mounted so activating the wizard does not remount the
// slot — the dropdown whose opening activates it would otherwise close again. Its
// mount/unmount autofocus belongs to that permanent lifecycle rather than to
// activation, so focus movement is owned here instead.
function keepFocusWhereItIs(event: Event) {
  event.preventDefault()
}

// A dropdown inside the wizard renders its menu within the overlay, so the Escape that
// closes the menu bubbles here before reka-ui's document listener sees it. That Escape
// belongs to the menu alone; closing the wizard too would throw away the dialog the
// user was still working in.
function closeOnEscape(event: KeyboardEvent) {
  const escapedFromMenu = (event.target as Element | null)?.closest('[role="menu"]')
  if (props.activated && !escapedFromMenu) {
    emit('unfocus')
  }
}

function overlayElement() {
  return overlay.value?.$el as HTMLElement | undefined
}

function overlayHoldsFocus() {
  const element = overlayElement()
  return Boolean(element && document.activeElement && element.contains(document.activeElement))
}

// Every later activation comes from inside — opening one of the wizard's dropdowns is
// what activates it — and moving focus then would close that dropdown again. Only the
// very first run can have focus somewhere else, because the wizard starts activated
// when the URL already carries a subject, and the trap then comes up with focus still
// on the document body: trapped outside the scope doing the trapping, which leaves a
// keyboard user with nowhere to go and no way back in.
//
// The test has to be "is this the initial activation", not "is focus inside the
// overlay": reka-ui renders menu content in a portal outside the overlay element, so
// an open dropdown's focus does not look like it is inside, and pulling focus in
// would close the dropdown that just activated the wizard.
async function pullFocusIn() {
  await nextTick()
  const element = overlayElement()
  // the overlay can be deactivated while this tick is pending, and focusing a
  // closed dialog would take focus off whatever the user moved to
  if (!element || !props.activated || overlayHoldsFocus()) {
    return
  }
  element.setAttribute('tabindex', '-1')
  element.focus()
}

let awaitingFirstRun = true

watch(
  () => props.activated,
  async (activated) => {
    const isInitialRun = awaitingFirstRun
    awaitingFirstRun = false
    if (activated) {
      elementFocusedBeforeActivation.value = document.activeElement as HTMLElement | null
      if (isInitialRun) {
        await pullFocusIn()
      }
      return
    }
    const returnFocusTo = overlayHoldsFocus() ? elementFocusedBeforeActivation.value : null
    elementFocusedBeforeActivation.value = null
    // the focus trap is torn down on the next tick, and until then it pulls any
    // outbound focus straight back into the overlay
    await nextTick()
    returnFocusTo?.focus()
  },
  { immediate: true }
)
</script>

<template>
  <div>
    <FocusScope
      ref="overlay"
      data-testid="wizard-overlay"
      loop
      :trapped="activated"
      :role="activated ? 'dialog' : undefined"
      :aria-modal="activated ? 'true' : undefined"
      :aria-label="(activated && label) || undefined"
      class="relative mx-auto w-[95%] max-w-[95%] rounded-[10px] border border-gray-400 px-9 py-6 transition-all duration-500 focus:outline-none"
      :class="[
        THEME_CLASSES[theme],
        { '-mt-2.5 mb-2.5 z-[1002] shadow-[0_0_60px_8px_rgba(0,0,0,0.4)]': activated }
      ]"
      @mount-auto-focus="keepFocusWhereItIs"
      @unmount-auto-focus="keepFocusWhereItIs"
      @keydown.esc="closeOnEscape"
    >
      <slot />
    </FocusScope>

    <div
      v-if="activated"
      data-testid="wizard-backdrop"
      aria-hidden="true"
      class="fixed inset-0 z-[1001] bg-black opacity-55"
    />
  </div>
</template>
